import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";

const R = (f) => `/images/real/${f}`;

export function TomsBar() {
  return (
    <section className="bg-ivory py-20 md:py-28" data-testid="home-toms-bar">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-5 md:px-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <div className="relative aspect-[4/3] overflow-hidden">
            <img src={R("lodge-bar.jpg")} alt="Tom's Bar — the traditional Irish-style private bar at Collanmore Island Lodge" loading="lazy" className="h-full w-full object-cover" />
          </div>
        </Reveal>
        <div>
          <Reveal>
            <Eyebrow>The Craic</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-medium leading-tight text-navy md:text-5xl" data-testid="toms-bar-heading">
              Your Own Private <em className="text-gold">Irish Pub</em>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-xl text-sm font-light leading-relaxed text-navy/75 md:text-base">
              One of the lodge's most distinctive features is Tom's Bar, our traditional Irish-style private bar. Bring your favourite drinks, gather around the bar and enjoy the atmosphere of an Irish pub without ever leaving the island.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-navy/75 md:text-base">
              Our cocktail-style bar provides another entertaining space for celebrations and social gatherings.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/private-island-accommodation-westport/"
              data-testid="toms-bar-link"
              className="group mt-8 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-navy transition-colors hover:text-gold"
            >
              Explore the Lodge
              <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function AdventureRelax() {
  return (
    <section className="bg-ivory pb-20 md:pb-28" data-testid="home-adventure-relax">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <Reveal>
            <div className="group overflow-hidden">
              <img src={R("kayak-aerial.jpg")} alt="Kayakers paddling the sheltered waters of Clew Bay" loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
            </div>
            <h2 className="mt-8 font-display text-3xl font-medium text-navy md:text-4xl" data-testid="adventure-heading">
              Adventure on Your Doorstep
            </h2>
            <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-navy/75 md:text-base">
              Explore the beautiful coastline of Clew Bay with optional guided sea kayaking, stand-up paddleboarding, sea safari adventures and island activities. Whether you're looking for an active weekend or a relaxing island retreat, our team can help tailor the experience to your group.
            </p>
            <Link to="/adventure-activities-westport/" data-testid="adventure-link" className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-navy transition-colors hover:text-gold">
              Adventure Activities <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="group overflow-hidden">
              <img src={R("hottub-bubbles.webp")} alt="The outdoor hot tub bubbling away beside the deck, with Clew Bay beyond" loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
            </div>
            <h2 className="mt-8 font-display text-3xl font-medium text-navy md:text-4xl" data-testid="relax-heading">
              Relax, Unwind and Recharge
            </h2>
            <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-navy/75 md:text-base">
              Enjoy our beach sauna overlooking the Atlantic, take in the sea air, relax in the hot tub during scheduled access times or simply enjoy the peaceful island surroundings.
            </p>
            <Link to="/beach-sauna-westport/" data-testid="sauna-link" className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-navy transition-colors hover:text-gold">
              Beach Sauna &amp; Wellness <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const OCCASIONS = [
  { label: "Hen Party Weekends", to: "/hen-parties-westport/", img: R("hen-party-lodge.jpg"), alt: "A beach-themed hen party in the lodge" },
  { label: "Stag Party Weekends", to: "/stag-parties-westport/", img: R("rib-wake.jpg"), alt: "Our RIB carving through Clew Bay" },
  { label: "Birthdays & Family Celebrations", to: "/private-island-celebrations/", img: R("sumo-lawn.jpg"), alt: "Sumo suit games on the lodge lawn" },
  { label: "Corporate Retreats & Team Building", to: "/corporate-retreats-mayo/", img: R("lodge-deck-day.jpg"), alt: "The sun deck above Clew Bay" },
  { label: "Traitors Island Mystery Weekends", to: "/traitors-island-experience/", img: R("traitors-island-poster.webp"), alt: "Traitors Island poster — an immersive island mystery weekend at Collanmore Island Lodge" },
  { label: "Weddings & Private Events", to: "/private-island-weddings-mayo/", img: R("island-wedding-aerial.jpg"), alt: "A wedding gathering on the island, seen from the air" },
];

export function Occasions() {
  return (
    <section className="bg-ivory pb-20 md:pb-28" data-testid="home-occasions">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="mb-12 max-w-2xl">
          <Reveal>
            <Eyebrow>An Island for Every Occasion</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-medium leading-tight text-navy md:text-5xl" data-testid="occasions-heading">
              Plan the weekend <em className="text-gold">they'll never forget</em>
            </h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {OCCASIONS.map((o, i) => (
            <Reveal key={o.to} delay={(i % 3) * 0.07}>
              <Link
                to={o.to}
                data-testid={`occasion-card-${i + 1}`}
                className="group relative block h-72 overflow-hidden md:h-80"
              >
                <img src={o.img} alt={o.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <span className="block h-px w-10 bg-gold transition-all duration-700 group-hover:w-16" />
                  <h3 className="mt-3 font-display text-2xl font-medium text-ivory md:text-[1.6rem]">{o.label}</h3>
                  <span className="mt-2 inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest2 text-gold-2">
                    Discover <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function MinutesFromWestport() {
  return (
    <section className="letterbox relative overflow-hidden py-28 md:py-36" data-testid="home-minutes">
      <img src={R("clewbay-sailboats.jpg")} alt="Sailboats drifting between the green islands of Clew Bay, seen from the air" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-navy/70" />
      <div className="relative z-10 mx-auto max-w-3xl px-5 text-center md:px-10">
        <Reveal>
          <div className="flex justify-center">
            <Eyebrow light>Getting Here</Eyebrow>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-4xl font-medium leading-tight text-ivory md:text-6xl" data-testid="minutes-heading">
            Just minutes from Westport, <em className="text-gold-2">a world away from ordinary</em>
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mx-auto mt-6 max-w-2xl text-sm font-light leading-relaxed text-ivory/85 md:text-base" data-testid="minutes-copy">
            Collanmore Island Lodge is reached from Rosmoney Pier, approximately 12 minutes by road from Westport town, followed by a short high-speed boat transfer of approximately two minutes. The crossing is part of the experience, taking you from the mainland to the beautiful surroundings of Clew Bay.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
